/**
 * Validation de la cohérence entre l'email du référent et l'entreprise déclarée
 */

import { extractDomain } from './emailDomains';

interface EmailNameCoherenceResult {
  score: number; // 0-100
  confidence: 'high' | 'medium' | 'low' | 'none';
  matches: {
    firstName: boolean;
    lastName: boolean;
    firstInitial: boolean;
    lastInitial: boolean;
  };
  details: string;
}

interface CompanyEmailCoherenceResult {
  isCoherent: boolean;
  confidence: 'high' | 'medium' | 'low';
  reason: string;
  details?: {
    emailDomain: string;
    expectedCompany: string;
    declaredCompany?: string;
    stillWorksAtCompany?: boolean;
    nameCoherence?: EmailNameCoherenceResult;
  };
}

/**
 * Extrait le nom probable de l'entreprise depuis un domaine email
 */
function extractCompanyFromDomain(domain: string): string {
  if (!domain) return '';
  
  // Nettoyer le domaine
  let company = domain.toLowerCase();
  
  // Supprimer les extensions communes
  company = company.replace(/\.(com|fr|org|net|co\.uk|de|it|es|ch|be|ca|au)$/, '');
  
  // Supprimer les sous-domaines courants
  company = company.replace(/^(www|mail|email|smtp|webmail|corporate|corp)\./g, '');
  
  // Remplacer les tirets et underscores par des espaces
  company = company.replace(/[-_]/g, ' ');
  
  // Capitaliser
  return company.split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Normalise le nom d'une entreprise pour la comparaison
 */
function normalizeCompanyName(companyName: string): string {
  if (!companyName) return '';
  
  return companyName
    .toLowerCase()
    .trim()
    // Supprimer les suffixes juridiques courants
    .replace(/\s+(ltd|limited|inc|incorporated|corp|corporation|sa|sas|sarl|gmbh|ag|bv|nv|oy|ab|as|spa|srl|llc|plc|pty|co)\.?$/g, '')
    // Supprimer la ponctuation
    .replace(/[.,;:!?()[\]{}'"]/g, '')
    // Normaliser les espaces
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Calcule la similarité entre deux chaînes (algorithme de Levenshtein simplifié)
 */
function calculateSimilarity(str1: string, str2: string): number {
  if (!str1 || !str2) return 0;
  
  const longer = str1.length > str2.length ? str1 : str2;
  const shorter = str1.length > str2.length ? str2 : str1;
  
  if (longer.length === 0) return 1;
  
  // Vérification de correspondance exacte
  if (longer.includes(shorter) || shorter.includes(longer)) {
    return 0.8;
  }
  
  // Vérification de mots communs
  const words1 = str1.split(' ');
  const words2 = str2.split(' ');
  const commonWords = words1.filter(word => 
    word.length > 2 && words2.some(w => w.includes(word) || word.includes(w))
  );
  
  if (commonWords.length > 0) {
    return Math.min(0.7, commonWords.length / Math.max(words1.length, words2.length));
  }
  
  return 0;
}

/**
 * Normalise une chaîne pour la comparaison (supprime accents, ponctuation, espaces)
 */
function normalizeString(str: string): string {
  if (!str) return '';
  
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Supprimer les accents
    .replace(/[^a-z0-9]/g, '') // Garder seulement lettres et chiffres
    .trim();
}

/**
 * Extrait les tokens possibles d'un nom (gère les noms composés)
 */
function extractNameTokens(name: string): string[] {
  if (!name) return [];
  
  const normalized = normalizeString(name);
  const tokens: string[] = [normalized];
  
  // Si le nom contient des tirets ou espaces, ajouter les parties séparées
  const parts = name.toLowerCase().split(/[-\s]+/);
  if (parts.length > 1) {
    tokens.push(...parts.map(p => normalizeString(p)));
    // Ajouter aussi la version concaténée
    tokens.push(parts.map(p => normalizeString(p)).join(''));
  }
  
  return tokens.filter(t => t.length > 0);
}

/**
 * Vérifie la cohérence entre l'email et le nom/prénom du référent
 */
export function validateEmailNameCoherence(
  email: string,
  firstName: string,
  lastName: string
): EmailNameCoherenceResult {
  
  if (!email || !firstName || !lastName) {
    return {
      score: 0,
      confidence: 'none',
      matches: {
        firstName: false,
        lastName: false,
        firstInitial: false,
        lastInitial: false
      },
      details: 'Données insuffisantes pour la vérification'
    };
  }

  // Extraire la partie locale de l'email (avant le @)
  const emailLocal = email.split('@')[0].toLowerCase();
  
  // Normaliser pour la comparaison
  const normalizedEmailLocal = normalizeString(emailLocal);
  
  // Extraire les tokens des noms
  const firstNameTokens = extractNameTokens(firstName);
  const lastNameTokens = extractNameTokens(lastName);
  
  // Initiales
  const firstInitial = normalizeString(firstName.charAt(0));
  const lastInitial = normalizeString(lastName.charAt(0));
  
  // Vérifier les correspondances
  const matches = {
    firstName: firstNameTokens.some(token => normalizedEmailLocal.includes(token)),
    lastName: lastNameTokens.some(token => normalizedEmailLocal.includes(token)),
    firstInitial: normalizedEmailLocal.includes(firstInitial),
    lastInitial: normalizedEmailLocal.includes(lastInitial)
  };
  
  // Vérifier les emails génériques (score automatique de 0)
  const genericPrefixes = ['contact', 'info', 'admin', 'support', 'hello', 'rh', 'recrutement', 'hr'];
  if (genericPrefixes.some(prefix => emailLocal.startsWith(prefix))) {
    return {
      score: 0,
      confidence: 'none',
      matches,
      details: 'Email générique détecté'
    };
  }
  
  // Calculer le score
  let score = 0;
  let details = '';
  
  if (matches.firstName && matches.lastName) {
    score = 100;
    details = 'Prénom et nom complets trouvés dans l\'email';
  } else if (matches.lastName && matches.firstInitial) {
    score = 85;
    details = 'Nom complet et initiale du prénom trouvés';
  } else if (matches.firstName && matches.lastInitial) {
    score = 80;
    details = 'Prénom complet et initiale du nom trouvés';
  } else if (matches.lastName) {
    score = 65;
    details = 'Nom complet trouvé dans l\'email';
  } else if (matches.firstName) {
    score = 60;
    details = 'Prénom complet trouvé dans l\'email';
  } else if (matches.firstInitial && matches.lastInitial) {
    score = 40;
    details = 'Initiales trouvées dans l\'email';
  } else if (matches.lastInitial) {
    score = 25;
    details = 'Initiale du nom trouvée';
  } else if (matches.firstInitial) {
    score = 20;
    details = 'Initiale du prénom trouvée';
  } else {
    score = 0;
    details = 'Aucune correspondance avec le nom ou prénom';
  }
  
  // Déterminer la confidence (simplifié : >= 50 = conforme)
  let confidence: 'high' | 'medium' | 'low' | 'none';
  if (score >= 75) {
    confidence = 'high';
  } else if (score >= 50) {
    confidence = 'medium'; // Considéré comme conforme
  } else {
    confidence = 'low'; // Non-conforme
  }
  
  return {
    score,
    confidence,
    matches,
    details
  };
}

/**
 * Vérifie la cohérence entre l'email du référent et l'entreprise
 */
export function validateEmailCompanyCoherence(
  referentEmail: string,
  companyFromCV: string,
  stillWorksAtCompany?: boolean,
  _collaborationEndDate?: string | null,
  referentFirstName?: string,
  referentLastName?: string
): CompanyEmailCoherenceResult {
  const emailDomain = extractDomain(referentEmail);
  if (!emailDomain) {
    return {
      isCoherent: false,
      confidence: 'low' as const,
      reason: 'Domaine email invalide',
      details: {
        emailDomain: '',
        expectedCompany: companyFromCV
      }
    };
  }

  // Extraire le nom d'entreprise probable depuis l'email
  const companyFromEmail = extractCompanyFromDomain(emailDomain);
  
  // Normaliser les noms d'entreprise pour la comparaison
  const normalizedCVCompany = normalizeCompanyName(companyFromCV);
  const normalizedEmailCompany = normalizeCompanyName(companyFromEmail);
  
  // Calculer la similarité
  const similarity = calculateSimilarity(normalizedCVCompany, normalizedEmailCompany);
  
  // Déterminer si le référent travaille encore dans l'entreprise
  // IMPORTANT: On utilise UNIQUEMENT le champ stillWorksAtCompany
  // collaborationEndDate indique la fin de collaboration avec le CANDIDAT, pas avec l'entreprise
  const isCurrentEmployee = stillWorksAtCompany !== false; // Par défaut true si null/undefined (plus sécuritaire)
  
  // Convertir en boolean pour l'interface
  const stillWorksAtCompanyBoolean: boolean | undefined = 
    stillWorksAtCompany === true ? true : 
    stillWorksAtCompany === false ? false : 
    undefined;

  // Vérifier la cohérence nom/prénom si les données sont fournies
  let nameCoherence: EmailNameCoherenceResult | undefined;
  if (referentFirstName && referentLastName) {
    nameCoherence = validateEmailNameCoherence(
      referentEmail,
      referentFirstName,
      referentLastName
    );
  }

  // Cas 1: Domaine excellent (>= 0.7)
  if (similarity >= 0.7) {
    // Si on a le check nom/prénom
    if (nameCoherence) {
      if (nameCoherence.score >= 50) {
        // Domaine ET nom conformes (>= 50)
        return {
          isCoherent: true,
          confidence: nameCoherence.score >= 75 ? 'high' : 'medium',
          reason: 'Email cohérent avec l\'entreprise et le nom du référent',
          details: {
            emailDomain,
            expectedCompany: companyFromCV,
            stillWorksAtCompany: stillWorksAtCompanyBoolean,
            nameCoherence
          }
        };
      } else {
        // Domaine excellent mais nom ne correspond pas (< 50)
        return {
          isCoherent: true,
          confidence: 'medium',
          reason: 'Email cohérent avec l\'entreprise mais pas avec le nom du référent',
          details: {
            emailDomain,
            expectedCompany: companyFromCV,
            stillWorksAtCompany: stillWorksAtCompanyBoolean,
            nameCoherence
          }
        };
      }
    }
    
    // Pas de check nom/prénom, comportement original
    return {
      isCoherent: true,
      confidence: 'high',
      reason: 'Email cohérent avec l\'entreprise déclarée',
      details: {
        emailDomain,
        expectedCompany: companyFromCV,
        stillWorksAtCompany: stillWorksAtCompanyBoolean,
        nameCoherence
      }
    };
  }
  
  // Cas 2: Domaine moyen (0.4 - 0.7)
  if (similarity >= 0.4) {
    if (nameCoherence && nameCoherence.score >= 50) {
      // Domaine moyen mais nom conforme (>= 50) = renforce la confiance
      return {
        isCoherent: true,
        confidence: 'medium',
        reason: 'Email partiellement cohérent avec l\'entreprise, mais nom du référent confirmé',
        details: {
          emailDomain,
          expectedCompany: companyFromCV,
          stillWorksAtCompany: stillWorksAtCompanyBoolean,
          nameCoherence
        }
      };
    }
    
    return {
      isCoherent: true,
      confidence: 'medium',
      reason: 'Email partiellement cohérent avec l\'entreprise',
      details: {
        emailDomain,
        expectedCompany: companyFromCV,
        stillWorksAtCompany: stillWorksAtCompanyBoolean,
        nameCoherence
      }
    };
  }
  
  // Cas 3: Domaine faible (< 0.4)
  // Si le référent ne travaille PLUS dans l'entreprise de collaboration
  // → Email différent est NORMAL et ACCEPTABLE, MAIS nom doit quand même correspondre
  if (!isCurrentEmployee) {
    // ✅ PATCH CRITIQUE : Même ancien employé, si nom incohérent (< 50) = NON CONFORME
    if (nameCoherence && nameCoherence.score < 50) {
      return {
        isCoherent: false,
        confidence: 'low',
        reason: 'Email incohérent: nom ne correspond pas à l\'adresse email (même ancien employé)',
        details: {
          emailDomain,
          expectedCompany: companyFromCV,
          stillWorksAtCompany: false,
          nameCoherence
        }
      };
    }
    
    // Ancien employé + nom cohérent = ACCEPTABLE
    const result = {
      isCoherent: true,
      confidence: 'medium' as const,
      reason: 'Email différent acceptable (ancien employé de l\'entreprise de collaboration)',
      details: {
        emailDomain,
        expectedCompany: companyFromCV,
        stillWorksAtCompany: false,
        nameCoherence
      }
    };
    
    return result;
  }
  
  // LOGIQUE STRICTE : Si nom ne correspond pas (< 50) ET travaille encore dans l'entreprise
  if (nameCoherence && nameCoherence.score < 50 && isCurrentEmployee) {
    return {
      isCoherent: false, // CHANGEMENT : maintenant NON CONFORME
      confidence: 'low',
      reason: 'Email incohérent: nom ne correspond pas à l\'adresse email',
      details: {
        emailDomain,
        expectedCompany: companyFromCV,
        stillWorksAtCompany: stillWorksAtCompanyBoolean,
        nameCoherence
      }
    };
  }
  
  // Si le nom correspond bien malgré le domaine différent (>= 50)
  if (nameCoherence && nameCoherence.score >= 50) {
    return {
      isCoherent: true,
      confidence: 'low',
      reason: 'Domaine différent mais nom du référent confirmé dans l\'email',
      details: {
        emailDomain,
        expectedCompany: companyFromCV,
        stillWorksAtCompany: stillWorksAtCompanyBoolean,
        nameCoherence
      }
    };
  }
  
  // CAS PROBLÉMATIQUE : 
  // Référent prétend encore travailler dans l'entreprise de collaboration
  // MAIS son email est d'une entreprise complètement différente
  // → SUSPECT !
  const finalResult = {
    isCoherent: false,
    confidence: 'low' as const,
    reason: nameCoherence 
      ? 'Email incohérent: prétend encore travailler dans l\'entreprise mais email différent'
      : 'Email incohérent avec l\'entreprise et le nom du référent (employé actuel déclaré)',
    details: {
      emailDomain,
      expectedCompany: companyFromCV,
      stillWorksAtCompany: stillWorksAtCompanyBoolean,
      nameCoherence
    }
  };
  
  return finalResult;
}

/**
 * Analyse la cohérence email-entreprise pour tous les référents d'une campagne
 */
export function analyzeAllReferentsEmailCoherence(
  referents: Array<{
    email: string;
    companyFromCV: string;
    stillWorksAtCompany?: boolean;
    collaborationEndDate?: string | null;
    firstName?: string;
    lastName?: string;
  }>
): {
  overallScore: number;
  coherentCount: number;
  incoherentCount: number;
  results: Array<CompanyEmailCoherenceResult & { referentEmail: string }>;
} {
  
  const results = referents.map(referent => ({
    referentEmail: referent.email,
    ...validateEmailCompanyCoherence(
      referent.email,
      referent.companyFromCV,
      referent.stillWorksAtCompany,
      referent.collaborationEndDate,
      referent.firstName,
      referent.lastName
    )
  }));
  
  const coherentCount = results.filter(r => r.isCoherent).length;
  const incoherentCount = results.length - coherentCount;
  const overallScore = referents.length > 0 ? (coherentCount / referents.length) * 100 : 0;
  
  return {
    overallScore,
    coherentCount,
    incoherentCount,
    results
  };
}

/**
 * Fonction principale de validation email professionnel avec logique bloquante
 * Remplace validateProfessionalEmail() pour unifier la logique
 */
export function checkEmailProfessional(
  email: string,
  companyFromCV?: string,
  referentFirstName?: string,
  referentLastName?: string,
  stillWorksAtCompany?: boolean
): {
  isValid: boolean;
  isProfessional: boolean;
  level: 'block' | 'warning' | 'ok';
  reason: string;
  emailCheckStatus: 'professional' | 'personal' | 'disposable' | 'invalid';
  emailCheckDescription: string;
  coherenceScore?: number;
} {
  
  // Niveau 1 : Validation format basique
  if (!email || typeof email !== 'string') {
    return {
      isValid: false,
      isProfessional: false,
      level: 'block',
      reason: 'Email requis',
      emailCheckStatus: 'invalid',
      emailCheckDescription: 'Adresse email manquante ou invalide'
    };
  }

  const cleanEmail = email.toLowerCase().trim();
  const domain = cleanEmail.split('@')[1];
  
  if (!domain) {
    return {
      isValid: false,
      isProfessional: false,
      level: 'block',
      reason: 'Format email invalide',
      emailCheckStatus: 'invalid',
      emailCheckDescription: 'Format d\'adresse email invalide'
    };
  }

  // Listes complètes depuis emailDomains.ts
  const personalDomains = [
    // Grands fournisseurs internationaux
    'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com',
    'live.com', 'icloud.com', 'aol.com', 'protonmail.com',
    'mail.com', 'gmx.com', 'zoho.com', 'yandex.com',
    
    // Français
    'free.fr', 'orange.fr', 'sfr.fr', 'wanadoo.fr', 'laposte.net',
    'bbox.fr', 'numericable.fr', 'club-internet.fr', 'cegetel.net',
    
    // Autres pays européens
    'web.de', 'gmx.de', 't-online.de', 'freenet.de',
    'libero.it', 'virgilio.it', 'tiscali.it',
    'mail.ru', 'yandex.ru', 'rambler.ru',
    
    // Asie
    'qq.com', '163.com', '126.com', 'sina.com',
    'naver.com', 'daum.net', 'hanmail.net'
  ];

  const disposableDomains = [
    'mailinator.com', 'guerrillamail.com', 'temp-mail.org',
    'yopmail.com', '10minutemail.com', 'throwaway.email',
    'maildrop.cc', 'sharklasers.com', 'trashmail.com',
    'tempmail.net', 'getnada.com', 'mohmal.com',
    'mailcatch.com', 'mailnesia.com', 'spamgourmet.com'
  ];

  // Niveau 2 : Vérification domaines jetables (BLOQUANT)
  if (disposableDomains.includes(domain)) {
    return {
      isValid: false,
      isProfessional: false,
      level: 'block',
      reason: 'Email jetable interdit',
      emailCheckStatus: 'disposable',
      emailCheckDescription: 'Adresse email temporaire ou jetable détectée'
    };
  }

  // Niveau 3 : Vérification domaines personnels (BLOQUANT)
  if (personalDomains.includes(domain)) {
    return {
      isValid: false,
      isProfessional: false,
      level: 'block',
      reason: 'Email professionnel requis',
      emailCheckStatus: 'personal',
      emailCheckDescription: 'Adresse email personnelle détectée (Gmail, Yahoo, etc.)'
    };
  }

  // Email professionnel détecté - Analyse de cohérence avancée si données disponibles
  let coherenceScore = 100;
  let coherenceDetails = 'Email professionnel validé';

  if (companyFromCV) {
    const coherenceResult = validateEmailCompanyCoherence(
      cleanEmail,
      companyFromCV,
      stillWorksAtCompany,
      null, // collaborationEndDate
      referentFirstName,
      referentLastName
    );

    coherenceScore = coherenceResult.confidence === 'high' ? 100 : 
                    coherenceResult.confidence === 'medium' ? 75 : 50;

    if (!coherenceResult.isCoherent) {
      // LOGIQUE STRICTE : Si nom incohérent ET travaille encore → NON CONFORME
      return {
        isValid: true, // Format valide mais incohérent
        isProfessional: false, // CHANGEMENT : maintenant NON PROFESSIONNEL
        level: 'warning',
        reason: coherenceResult.reason,
        emailCheckStatus: 'professional',
        emailCheckDescription: `Email professionnel mais ${coherenceResult.reason.toLowerCase()}`,
        coherenceScore
      };
    }

    coherenceDetails = coherenceResult.reason;
  }

  // Email professionnel valide et cohérent
  return {
    isValid: true,
    isProfessional: true,
    level: 'ok',
    reason: 'Email professionnel valide',
    emailCheckStatus: 'professional',
    emailCheckDescription: coherenceDetails,
    coherenceScore
  };
}

/**
 * Suggestions d'amélioration pour les emails incohérents
 */
export function suggestEmailImprovements(
  referentEmail: string,
  expectedCompany: string
): string[] {
  const emailDomain = extractDomain(referentEmail);
  const localPart = referentEmail.split('@')[0];
  
  if (!emailDomain || !localPart) return [];
  
  // Générer des suggestions basées sur l'entreprise attendue
  const normalizedCompany = normalizeCompanyName(expectedCompany);
  const companyDomain = normalizedCompany.replace(/\s+/g, '').toLowerCase();
  
  const suggestions = [
    `${localPart}@${companyDomain}.com`,
    `${localPart}@${companyDomain}.fr`,
    `${localPart}@${companyDomain}.org`,
  ];
  
  // Ajouter des variations courantes
  if (companyDomain.length > 3) {
    suggestions.push(`${localPart}@${companyDomain.substring(0, 6)}.com`);
  }
  
  return suggestions.slice(0, 3);
}